import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from '../users.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { NgForm } from '@angular/forms';
import { EnterpriseService } from 'src/app/enterprise/enterprise.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { PermissionsService } from 'src/app/permissions/permissions.service';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-edit-user',
	templateUrl: './edit-user.component.html'
})
export class EditUserComponent implements OnInit {
	activePage: 'user_data' | 'email_subscription' = 'user_data';

	userId: any;
	user: any = {
		is_admin: false,
		email_confirmed: false,
		phone_confirmed: false,
		test_user: false,
		force_password_change: true,
		special_user: false,
		account_deactivate: false,
		is_two_sv_enabled: false,
		two_sv_enabled_by: this.usersService.TWO_SV_ENABLED_OPTIONS.NONE, // Initialize with NONE for new users
		email_subscription_list: {
			connectivity_verification: false,
			purchase_order_notifications: false,
			engineering : false,
			device_management_notifications: false,
			user_registration_request: false,
			device_operations: false,
			contact_us: false,
			maintenance_report: false,
			debug_records: false
		}
	};

	readonly subscriptionsTranslationKeys = {
		connectivity_verification: 'connectivity_verification.connectivity_verification',
		purchase_order_notifications: 'orders.purchase_order_notifications',
		engineering : 'g.engineering',
		device_management_notifications: 'device_management.device_management_notifications',
		user_registration_request: 'action_log.user_registration_request',
		device_operations: 'g.device_operations',
		contact_us: 'contact_us.title',
		maintenance_report: 'nav.maintenance_report',
		debug_records: 'g.debug_records',
	}

	currentUser: any = {};
	isAdminEdit: boolean = false;
	isAddNewUser: boolean = false;
	hasAuthCode: boolean = false;
	enterpriseList: any = [];
	permissionList: any = [];
	originalTwoSVEnabledBy: string | null = null;
	selectedEnterprise: any = null;

	handleEnterprise2SVSettingsInterval: any;
	langList: any = [
		{value: 'en', label: 'English'},
		{value: 'fr', label: 'Français'},
		{value: 'es', label: 'Español'}
	];

	verificationPassword: string;

	@ViewChild("editUserFrm") editUserFrm:NgForm;
	@ViewChild("deleteAccountModal", { static: true }) deleteAccountModal;
	@ViewChild("RemoveBlockModal", { static: true }) RemoveBlockModal;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private navigation: NavigationService,
		public usersService: UsersService,
		private notificationMessage: NotificationMessageService,
		private enterpriseService: EnterpriseService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private permissionsService: PermissionsService,
		private translateService: TranslateService
	) { }

	ngOnInit() {
		this.sideMenuService.hide();
		this.isAdminEdit = this.route.snapshot.data['isAdminEdit'];

		if(this.isAdminEdit){
			this.route.params.subscribe( (params: any) => {
				this.userId = +params.userId || 0;
				let hasAddAccess  = !this.userId && this.usersService.hasAccessFunction('add_user_admin');
				let hasEditAccess = this.userId && this.usersService.hasAccessFunction('edit_user_admin');

				if(!hasEditAccess && !hasAddAccess){
					this.notificationMessage.setMessage('globalAuthMsg');
					this.router.navigate(['/'], {})
				}

				if(this.userId > 0)
					this.getUserInfo(this.userId);
				else {
					this.isAddNewUser = true;
					this.commonService.setPageTabTitle(`${this.translateService.instant('g.add')} ${this.translateService.instant('g.user')}`);
				}

				let source = hasAddAccess ? 'add_user' : 'edit_user';

				this.enterpriseService.getEnterprises({source}).subscribe((data) => {
					this.enterpriseList = data;
				});
				this.permissionsService.getPermissionsList('user').subscribe((data) => {
					this.permissionList = data;
				});
			});

		} else {
			this.currentUser = this.usersService.getCurrentUser();
			this.userId = this.currentUser.id;
			this.getUserInfo(this.userId);
		}

		this.handleEnterprise2SVSettingsInterval = setInterval(() => {
			if (this.user.enterprise_id && this.enterpriseList.length) {
				clearInterval(this.handleEnterprise2SVSettingsInterval);
				this.selectedEnterprise = this.enterpriseList.find(e => e.id === this.user.enterprise_id);
				this.handleEnterprise2SVSettings();
			}
		}, 500);
	}

	ngAfterViewInit() {
		this.deleteAccountModal.onClose.subscribe(
			(ok) => {
				if(ok)
					this.deleteUserAccount();
				this.verificationPassword	= '';
			}
		);

		this.RemoveBlockModal.onClose.subscribe(
			(ok) => {
				if(ok)
					this.removeUserBlock();
			}
		);
	}

	goBack() {
		this.navigation.goBack('/');
	}

	editUser() {
		let formFields = this.editUserFrm.value;
		formFields.id = this.userId;

		if(!this.hasAuthCode)
			formFields.auth_code = "";

		this.fill2SVEnabledBy(formFields);
		delete formFields.is_two_sv_enabled;

		if(!formFields.password)
			delete formFields.password;

		for(const subscription of Object.keys(this.user.email_subscription_list)) {
			formFields[`email_subscription_list_${subscription}`] = this.user.email_subscription_list[subscription];
		}

		this.usersService.editUser(this.userId, formFields, {isAdminEdit: this.isAdminEdit}).subscribe((data: any) => {
			switch (data) {
				case 1:
					this.notificationMessage.setMessage('globalSuccessMsg');
					this.goBack();
					break;
				case 2:
					this.notificationMessage.setMessage('translate|g.invalid_input', {clearOnXTimeNavigate: 1});
					break;
				case 3:
					this.notificationMessage.setMessage('translate|users.email_used', {clearOnXTimeNavigate: 1});
					break;
				case 4:
					this.notificationMessage.setMessage('translate|users.phone_used', {clearOnXTimeNavigate: 1});
					break;
				case 6:
					this.notificationMessage.setMessage('translate|users.password_reused', {clearOnXTimeNavigate: 1});
					break;
			}
		});
	}

	getUserInfo(userId) {
		this.usersService.getUsersInfo(userId, {getUserIsBlocked: true}).subscribe((data:any) => {
			data = data[0];
			data.enterprise_id = (data.enterprise_id == 0 ? null : data.enterprise_id);
			data.email_subscription_list = {...this.user.email_subscription_list, ...data.email_subscription_list};

			// Store the original two_sv_enabled_by value for permission check
			this.originalTwoSVEnabledBy = data.two_sv_enabled_by;

			this.fillIs2SVEnabled(data);
			this.user = data;
			this.hasAuthCode = this.user.auth_code && this.user.auth_code.length > 0;
			this.commonService.setPageTabTitle(`${this.translateService.instant('users.edit_user')} - ${this.user.first_name} ${this.user.last_name}`);
		});
	}
	onChangeHasAuthCode(val) {
		if(val.checked && (!this.user.auth_code || this.user.auth_code.length === 0))
			this.generateRandomAuthCode();
	}
	generateRandomAuthCode() {
		this.user.auth_code = this.commonService.genRandomString(8);
	}

	deleteUserAccount() {
		this.usersService.deleteUserAccount(this.verificationPassword).subscribe(response => {
			if (response.api_status == 2)
				return this.notificationMessage.setMessage('translate|login_register.incorrect_password', {clearOnXTimeNavigate: 1});

			this.usersService.setCurrentUser({});
			this.navigation.setPreviousUrl(null, true);
			this.router.navigate(['/login']);

		});
	}

	removeUserBlock() {
		this.usersService.removeUserBlock(this.userId).subscribe(res => {
			this.notificationMessage.setMessage('globalSuccessMsg');
			this.user.is_blocked = false;
		});
	}

	setActivePage(page: 'email_subscription' | 'user_data') {
		this.activePage = page;
	}

	fillIs2SVEnabled(data) {
		// Set is_two_sv_enabled based on whether it's enabled by any method
		if (this.isAdminEdit)
			data.is_two_sv_enabled = ![this.usersService.TWO_SV_ENABLED_OPTIONS.NONE, this.usersService.TWO_SV_ENABLED_OPTIONS.USER].includes(data.two_sv_enabled_by);
		else
			data.is_two_sv_enabled = data.two_sv_enabled_by !== this.usersService.TWO_SV_ENABLED_OPTIONS.NONE;
	}

	fill2SVEnabledBy(formFields) {
		// Default to NONE
		formFields.two_sv_enabled_by = this.usersService.TWO_SV_ENABLED_OPTIONS.NONE;

		if (this.user.is_two_sv_enabled) {
			if (this.isAdminEdit) {
				// If admin editing and enterprise has 2SV enabled, mark as ENTERPRISE, otherwise ADMIN
				formFields.two_sv_enabled_by = this.selectedEnterprise?.is_two_sv_enabled
					? this.usersService.TWO_SV_ENABLED_OPTIONS.ENTERPRISE
					: this.usersService.TWO_SV_ENABLED_OPTIONS.ADMIN;
			} else {
				// Enabled by the user themselves
				formFields.two_sv_enabled_by = this.originalTwoSVEnabledBy;
				if ([this.usersService.TWO_SV_ENABLED_OPTIONS.NONE, this.usersService.TWO_SV_ENABLED_OPTIONS.USER].includes(this.originalTwoSVEnabledBy))
					formFields.two_sv_enabled_by = this.usersService.TWO_SV_ENABLED_OPTIONS.USER;
			}
		}
	}

	handleEnterprise2SVSettings() {
		if (this.selectedEnterprise?.is_two_sv_enabled) {
			// If enterprise has 2SV enabled, force enable it for the user
			this.user.is_two_sv_enabled = true;
			this.user.two_sv_enabled_by = this.usersService.TWO_SV_ENABLED_OPTIONS.ENTERPRISE;
		} else if (this.user.two_sv_enabled_by === this.usersService.TWO_SV_ENABLED_OPTIONS.ENTERPRISE) {
			// If enterprise no longer has 2SV enabled but user was enforced by enterprise
			// Reset to disabled state
			this.user.is_two_sv_enabled = false;
			this.user.two_sv_enabled_by = this.usersService.TWO_SV_ENABLED_OPTIONS.NONE;
			// Reset the originalTwoSVEnabledBy if switching away from an enterprise with 2SV
			this.originalTwoSVEnabledBy = this.usersService.TWO_SV_ENABLED_OPTIONS.NONE;
		}
	}
	
	onEnterpriseChange() {
		this.selectedEnterprise = this.enterpriseList.find(e => e.id === this.user.enterprise_id);
		this.handleEnterprise2SVSettings();
	}

	isTwoSVLockedByEnterprise(): boolean {
		if (this.isAdminEdit) {
			// For both edit and create - check if selected enterprise has 2SV enabled
			return this.selectedEnterprise?.is_two_sv_enabled === true;
		} else {
			// For user edit - check if 2SV was enabled by admin or enterprise
			return this.user.two_sv_enabled_by === this.usersService.TWO_SV_ENABLED_OPTIONS.ENTERPRISE || 
				this.user.two_sv_enabled_by === this.usersService.TWO_SV_ENABLED_OPTIONS.ADMIN;
		}
	}

	get2SVTooltipText() {
		return this.isAdminEdit ? 'admin_enable_2sv' : 'user_enable_2sv';
	}

	ngOnDestroy() {
		clearInterval(this.handleEnterprise2SVSettingsInterval);
	}
}
